import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Badge,
  List,
  ListItem,
  Icon,
  Button,
  Flex
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { fallbackTiers } from './Subscribe/tiersConfig';
import MarketingFooter from './MarketingFooter';
import { marketingHeadingStyles } from './marketingStyles';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const getSignupPath = (planId) => {
  const returnTo = `/client/purchases?planId=${encodeURIComponent(planId)}&source=pricing`;
  return `/client/sign-up?returnTo=${encodeURIComponent(returnTo)}`;
};

const PricingPage = () => {
  return (
    <Box fontFamily="var(--telivi-font-body)" sx={marketingHeadingStyles}>
      <Box bgGradient="linear(to-b, gray.50, white)" pt={{ base: 16, lg: 24 }} pb={{ base: 12, lg: 16 }}>
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center">
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Pricing
            </Text>
            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }} fontFamily="var(--telivi-font-heading)">
              Transparent tiers with isolated infrastructure
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Pick the level of operational coverage you need. Each tier includes dedicated
              infrastructure, databases, and tenant-specific domains.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {fallbackTiers.map((tier) => {
              const monthlyLabel = tier.monthlyPrice
                ? `${formatCurrency(tier.monthlyPrice)}/mo`
                : 'Starting at custom monthly rate';

              return (
                <Box
                  key={tier.id}
                  bg="white"
                  borderRadius="2xl"
                  border={tier.recommended ? '2px solid' : '1px solid'}
                  borderColor={tier.recommended ? 'blue.400' : 'gray.100'}
                  boxShadow={tier.recommended ? 'xl' : 'lg'}
                  p={8}
                >
                  <Stack spacing={4}>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Text color="blue.500" fontWeight="600">
                          Tier {tier.tier}
                        </Text>
                        <Heading as="h2" fontSize="2xl">
                          {tier.label}
                        </Heading>
                      </Box>
                      {tier.recommended ? <Badge colorScheme="blue">Recommended</Badge> : null}
                    </Flex>
                    <Text fontSize="lg">{monthlyLabel}</Text>
                    <Text color="gray.600">
                      Setup fee: {formatCurrency(tier.setupFee)} · Turnaround {tier.turnaroundDays} days
                    </Text>

                    <Box>
                      <Text fontWeight="600" mb={2}>
                        Services included
                      </Text>
                      <List spacing={2} color="gray.600">
                        {tier.servicesIncluded.map((service) => (
                          <ListItem key={service}>
                            <Flex align="center" gap={2}>
                              <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                              <Text>{service}</Text>
                            </Flex>
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    <Box>
                      <Text fontWeight="600" mb={2}>
                        Included dashboards
                      </Text>
                      <List spacing={2} color="gray.600">
                        {tier.dashboardsIncluded.map((dashboard) => (
                          <ListItem key={dashboard}>
                            <Flex align="center" gap={2}>
                              <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                              <Text>{dashboard}</Text>
                            </Flex>
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    <Box>
                      <Text fontWeight="600" mb={2}>
                        Platform guarantees
                      </Text>
                      <List spacing={2} color="gray.600">
                        {tier.platformGuarantees.map((guarantee) => (
                          <ListItem key={guarantee}>
                            <Flex align="center" gap={2}>
                              <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                              <Text>{guarantee}</Text>
                            </Flex>
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    <Button as={RouterLink} to={getSignupPath(tier.id)} colorScheme="blue" size="lg">
                      Subscribe
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default PricingPage;
